<template>
  <div
    ref="profileAssignUser"
    v-on-click-outside="() => (activeDropDown = null)"
    class="inline-block"
    :data-cy="
      isLendflowUser ? 'profile-assign-user-lendflow' : 'profile-assign-user'
    "
  >
    <template v-if="roleIdentifier">
      <button
        type="button"
        class="relative mr-1 flex rounded-full text-left text-s"
        :class="{
          'bg-gray-200':
            userMapByIdentifier?.show && !userMapByIdentifier?.name,
        }"
        @click.stop="toggleMenu(roleIdentifier)"
      >
        <span :class="{ 'pl-2': !userMapByIdentifier?.name }">
          {{ userMapByIdentifier?.name || "-" }}
        </span>
        <span
          v-if="userMapByIdentifier?.show"
          class="inset-y-0 ml-2 flex cursor-pointer place-self-center pr-2"
        >
          <icon-base
            height="6"
            width="10"
            icon="dropdown-arrow"
            icon-color="#868D9C"
          />
        </span>
      </button>
      <lf-dropdown-with-infinite-query
        v-if="userMapByIdentifier?.show"
        filter-icon
        :query-options="{
          queryKey: userMapByIdentifier?.queryKey,
          queryFn: userMapByIdentifier?.getPaginatedOptions,
        }"
        accessor="full_name"
        identifier="id"
        include-none
        :value="userMapByIdentifier?.model?.id"
        :name="roleIdentifier"
        :active="roleIdentifier === activeDropDown"
        @change="(user) => handleUserSelection(userMapByIdentifier!, user)"
      />
    </template>
    <template v-else>
      <div class="flex items-center">
        <span
          v-if="isLendflowUser && !profile.is_for_client"
          class="mr-2 text-gray-400"
        >
          {{ t("COMMON.LENDFLOW") }}
        </span>
        <template v-for="item in usersMapping">
          <div
            v-if="item.show"
            :key="item.id"
            v-tooltip="{
              content: item.tooltip,
              onShow: () => item.id !== activeDropDown,
            }"
          >
            <div class="flex-wrap items-center">
              <button
                type="button"
                class="relative mr-1 flex rounded-full text-left text-s"
                @click.stop="toggleMenu(item.id)"
              >
                <lf-avatar
                  v-if="!!item.model"
                  :username="item.name"
                  :size="32"
                  :theme="item.color"
                />
                <span
                  v-else
                  class="cursor-pointer rounded-full p-2 hover:bg-gray-100"
                >
                  <icon-base :icon="item.icon" />
                </span>
              </button>
              <lf-dropdown-with-infinite-query
                filter-icon
                :query-options="{
                  queryKey: item.queryKey,
                  queryFn: item.getPaginatedOptions,
                }"
                accessor="full_name"
                identifier="id"
                include-none
                :value="item.model?.id"
                :name="item.id"
                :active="item.id === activeDropDown"
                :class="evaluatedPosition"
                @change="(user) => handleUserSelection(item, user)"
              />
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>
<script lang="ts">
import { computed, ref } from "vue";

import { Entity } from "@/enums/entityProfiles";
import { USER_BADGE_COLORS, USER_COLORS } from "@/helpers/constants";
import { usePromiseWrapper } from "@/hooks/common";
import { useIndividualProfileStore } from "@/stores/individualProfile";

import {
  type LfInfiniteQueryOptions,
  NONE_OPTION_ID,
} from "@/components/ui/inputs/LfDropdownWithInfiniteQuery.vue";

import type { AvatarTheme } from "@/components/ui/LFAvatar.vue";
import type { GetPaginatedUsersCallback } from "@/hooks/useUsersForFilters";
import type { IProfile } from "@/models/businesses";
import type { IUser, RoleIdentifier } from "@/models/users";
import type { PropType } from "vue";

interface UserMap {
  id: RoleIdentifier;
  model: IUser | null | undefined;
  name: string;
  show: boolean;
  tooltip: string;
  icon: string;
  color: AvatarTheme;
  getPaginatedOptions: GetPaginatedUsersCallback;
  queryKey: LfInfiniteQueryOptions["queryKey"];
  update: (value: string) => void;
}

const REMOVE_OPTION = "remove" as const;
</script>
<script setup lang="ts">
import { useWindowSize } from "@vueuse/core";
import { useI18n } from "vue-i18n";

import { formatUserFullName } from "@/helpers/formatting";
import { useAuth } from "@/hooks/auth";
import { useClients } from "@/hooks/clients";
import { useUsersForFilters } from "@/hooks/useUsersForFilters";
import useBusinessesStore from "@/stores/businesses";

import IconBase from "@/components/ui/IconBase.vue";
import LfAvatar from "@/components/ui/LFAvatar.vue";
import LfDropdownWithInfiniteQuery from "@/components/ui/inputs/LfDropdownWithInfiniteQuery.vue";

const emit = defineEmits<{
  (eventName: "changed", profile: Partial<IProfile>): void;
}>();

const props = defineProps({
  profile: {
    type: Object as PropType<Partial<IProfile>>,
    required: true,
  },
  type: {
    type: String as PropType<Entity>,
    default: "business",
  },
  roleIdentifier: {
    type: String as PropType<RoleIdentifier | null>,
    default: null,
  },
});

const individualProfileStore = useIndividualProfileStore();
const businessesProfileStore = useBusinessesStore();

const { t } = useI18n();
const { width: appWidth } = useWindowSize();

const { isLendflowUser } = useAuth();
const { clientCanDoUnderwriting } = useClients();
const {
  getAnalysts,
  getAnalystsQueryKey,
  getFundingAdvisors,
  getFundingAdvisorsQueryKey,
  getUnderwriters,
  getUnderwritersQueryKey,
  getClientAnalysts,
  getClientAnalystsQueryKey,
  getClientFundingAdvisors,
  getClientFundingAdvisorsQueryKey,
  getClientUnderwriters,
  getClientUnderwritersQueryKey,
} = useUsersForFilters({ ignoreAssignedDeals: true });

const profileAssignUser = ref<HTMLElement | null>(null);
const activeDropDown = ref<RoleIdentifier | null>(null);

const usersMapping = computed(() => {
  const mapped: Array<UserMap> = [
    {
      id: "underwriter_id",
      model: props.profile.underwriter,
      name: formatUserFullName(props.profile.underwriter),
      show: isLendflowUser && !props.profile.is_for_client,
      tooltip: `${t("DEALS.TABLE_HEADERS.UNDERWRITER")}: ${
        formatUserFullName(props.profile.underwriter) || t("COMMON.UNASSIGNED")
      }`,
      icon: "briefcase-checked",
      color: USER_BADGE_COLORS[USER_COLORS.BLUE],
      getPaginatedOptions: getUnderwriters,
      queryKey: getUnderwritersQueryKey,
      update: (value: string) => {
        update(value, "underwriter_id");
      },
    },
    {
      id: "funding_advisor_id",
      model: props.profile.funding_advisor,
      name: formatUserFullName(props.profile.funding_advisor),
      show: isLendflowUser && !props.profile.is_for_client,
      tooltip: `${t("DEALS.TABLE_HEADERS.FUNDING_ADVISOR")}: ${
        formatUserFullName(props.profile.funding_advisor) ||
        t("COMMON.UNASSIGNED")
      }`,
      icon: "chat-bubble",
      color: USER_BADGE_COLORS[USER_COLORS.GREEN],
      getPaginatedOptions: getFundingAdvisors,
      queryKey: getFundingAdvisorsQueryKey,
      update: (value: string) => {
        update(value, "funding_advisor_id");
      },
    },
    {
      id: "analyst_id",
      model: props.profile.analyst,
      name: formatUserFullName(props.profile.analyst),
      show: isLendflowUser && !props.profile.is_for_client,
      icon: "pie-chart",
      color: USER_BADGE_COLORS[USER_COLORS.MINT],
      tooltip: `${t("DEALS.TABLE_HEADERS.ANALYST")}: ${
        formatUserFullName(props.profile.analyst) || t("COMMON.UNASSIGNED")
      }`,
      getPaginatedOptions: getAnalysts,
      queryKey: getAnalystsQueryKey,
      update: (value: string) => {
        update(value, "analyst_id");
      },
    },
    {
      id: "client_underwriter_id",
      model: props.profile.client_underwriter,
      name: formatUserFullName(props.profile.client_underwriter),
      show: clientCanDoUnderwriting.value && !!props.profile.is_for_client,
      tooltip: `${t("DEALS.TABLE_HEADERS.UNDERWRITER")}: ${
        formatUserFullName(props.profile.client_underwriter) ||
        t("COMMON.UNASSIGNED")
      }`,
      icon: "briefcase-checked",
      color: USER_BADGE_COLORS[USER_COLORS.BLUE],
      getPaginatedOptions: getClientUnderwriters,
      queryKey: getClientUnderwritersQueryKey,
      update: (value: string) => {
        update(value, "client_underwriter_id");
      },
    },
    {
      id: "client_funding_advisor_id",
      model: props.profile.client_funding_advisor,
      name: formatUserFullName(props.profile.client_funding_advisor),
      show: clientCanDoUnderwriting.value && !!props.profile.is_for_client,
      tooltip: `${t("DEALS.TABLE_HEADERS.FUNDING_ADVISOR")}: ${
        formatUserFullName(props.profile.client_funding_advisor) ||
        t("COMMON.UNASSIGNED")
      }`,
      icon: "chat-bubble",
      color: USER_BADGE_COLORS[USER_COLORS.GREEN],
      getPaginatedOptions: getClientFundingAdvisors,
      queryKey: getClientFundingAdvisorsQueryKey,
      update: (value: string) => {
        update(value, "client_funding_advisor_id");
      },
    },
    {
      id: "client_analyst_id",
      model: props.profile.client_analyst,
      name: formatUserFullName(props.profile.client_analyst),
      show: clientCanDoUnderwriting.value && !!props.profile.is_for_client,
      tooltip: `${t("DEALS.TABLE_HEADERS.ANALYST")}: ${
        formatUserFullName(props.profile.client_analyst) ||
        t("COMMON.UNASSIGNED")
      }`,
      icon: "pie-chart",
      color: USER_BADGE_COLORS[USER_COLORS.MINT],
      getPaginatedOptions: getClientAnalysts,
      queryKey: getClientAnalystsQueryKey,
      update: (value: string) => {
        update(value, "client_analyst_id");
      },
    },
  ];

  return mapped;
});

const userMapByIdentifier = computed(() =>
  usersMapping.value.find(({ id }) => id === props.roleIdentifier)
);

const evaluatedPosition = computed(() => {
  if (profileAssignUser.value) {
    const menuRect = profileAssignUser.value.getBoundingClientRect();
    return menuRect.right + 100 > appWidth.value ? "menu-transform" : "";
  }

  return "";
});

const handleUserSelection = (map: UserMap, user: IUser | undefined) => {
  if (!user || String(user.id) === NONE_OPTION_ID) {
    map.update(REMOVE_OPTION);
  } else {
    map.update(String(user.id));
  }
};

const { fetchWrapper: update } = usePromiseWrapper(
  async (value: string, id: RoleIdentifier) => {
    try {
      const payload = {
        profileId: props.profile.id || "",
        params: { [id]: value === REMOVE_OPTION ? null : value },
      };
      const response =
        props.type === Entity.business
          ? await businessesProfileStore.updateBusinessEntityProfile(payload)
          : await individualProfileStore.updateIndividualProfile(payload);
      emit("changed", response);
    } finally {
      activeDropDown.value = null;
    }
  }
);

const toggleMenu = (id: RoleIdentifier | null) =>
  (activeDropDown.value = activeDropDown.value !== id ? id : null);
</script>
<style>
@reference "@/assets/tailwind.css";

.menu-transform > div.absolute {
  transform: translateX(-80%);
}
</style>
